/**
 * Pagination — Page navigation for product listings
 *
 * Displays "Showing X-Y of Z" text, page number buttons with ellipsis,
 * and prev/next navigation. Electric-blue active page, rounded-none buttons.
 *
 * Props:
 *   currentPage  — 1-based current page index
 *   totalPages   — total number of pages
 *   totalItems   — total item count for "Showing X-Y of Z" label
 *   pageSize     — items per page
 *   onPageChange — callback with new page number
 */

/**
 * Build an array of page numbers with ellipsis markers.
 * Always shows first, last, and up to 2 pages around current.
 */
function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set([1, totalPages]);

  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
    pages.add(i);
  }

  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];

  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push('...');
    }
    result.push(sorted[i]);
  }

  return result;
}

export default function Pagination({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 25,
  onPageChange,
}) {
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  const handlePage = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange?.(page);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6">
      {/* Showing label */}
      <p className="text-sm text-n5 font-sandvik-text">
        Showing{' '}
        <span className="text-carbon-black">
          {startItem}&ndash;{endItem}
        </span>{' '}
        of{' '}
        <span className="font-medium text-carbon-black">{totalItems}</span>
      </p>

      {/* Page controls */}
      <nav className="flex items-center gap-1" aria-label="Pagination">
        {/* Prev */}
        <button
          type="button"
          onClick={() => handlePage(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
          className={`
            inline-flex items-center justify-center w-10 h-10 rounded-none
            border border-n2 text-sm font-sandvik-text transition-colors
            ${currentPage === 1
              ? 'text-n3 cursor-not-allowed bg-white'
              : 'text-carbon-black hover:bg-n1'
            }
          `}
        >
          <span className="material-symbols-outlined text-lg">chevron_left</span>
        </button>

        {/* Page numbers */}
        {pageNumbers.map((page, idx) =>
          page === '...' ? (
            <span
              key={`ellipsis-${idx}`}
              className="inline-flex items-center justify-center w-10 h-10 text-n5 text-sm"
              aria-hidden="true"
            >
              &hellip;
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => handlePage(page)}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
              className={`
                inline-flex items-center justify-center w-10 h-10 rounded-none
                border text-sm font-sandvik-text font-medium transition-colors
                ${page === currentPage
                  ? 'bg-electric-blue text-white border-electric-blue'
                  : 'border-n2 text-carbon-black hover:bg-n1'
                }
              `}
            >
              {page}
            </button>
          )
        )}

        {/* Next */}
        <button
          type="button"
          onClick={() => handlePage(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
          className={`
            inline-flex items-center justify-center w-10 h-10 rounded-none
            border border-n2 text-sm font-sandvik-text transition-colors
            ${currentPage === totalPages
              ? 'text-n3 cursor-not-allowed bg-white'
              : 'text-carbon-black hover:bg-n1'
            }
          `}
        >
          <span className="material-symbols-outlined text-lg">chevron_right</span>
        </button>
      </nav>
    </div>
  );
}
